import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  AreaChartOutlined,
  BarChartOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  GatewayOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { apiClient } from "../../services/api/apiClient";
import { SURFACE_COLORS, fmtHa } from "./adminDashboardShared";

const { Text } = Typography;

interface SurfacePayload {
  role: "admin";
  scope: "surface";
  season: { id: number; label: string; year: number } | null;
  available_seasons: Array<{ id: number; label: string; year: number }>;
  kpis: {
    licensed_ha: number;
    audited_ha: number;
    productive_ha: number;
    netted_pct: number;
    netted_ha: number;
  };
  by_province: Array<{
    province_name: string;
    licensed: number;
    audited: number;
    productive: number;
    netted: number;
  }>;
  netted_breakdown: Array<{ bucket: string; ha: number }>;
  productive_breakdown: Array<{ bucket: string; ha: number }>;
  by_producer: Array<{
    producer_id: number;
    producer_name: string;
    province_name: string;
    farms_count: number;
    licensed_ha: number;
    audited_ha: number;
    productive_ha: number;
    netted_pct: number;
    has_pending_audits: boolean;
  }>;
}

// Mismo payload del scope `producer_breakdown` que usa el panel de
// producción — ya trae ha además de kg (ver _build_admin_producer_breakdown
// en el backend). El modal de superficie filtra las columnas que
// muestra.
interface SurfaceBreakdown {
  role: "admin";
  scope: "producer_breakdown";
  season: { id: number; label: string; year: number } | null;
  producer: { id: number; name: string | null } | null;
  audits: Array<{
    audit_id: number;
    reference: string;
    farm_name: string;
    start_date: string | null;
    finalized_at: string | null;
    status: string;
    audit_type: string;
    licensed_ha: number;
    audited_ha: number;
    productive_ha: number;
    is_under_net: boolean;
  }>;
}

interface Props {
  season: number | null;
}

export const AdminSurfacePanel = ({ season }: Props) => {
  const [payload, setPayload] = useState<SurfacePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado del modal "Ver desglose" para superficie — mismo patrón
  // que en el panel de producción.
  const [breakdownProducer, setBreakdownProducer] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [breakdown, setBreakdown] = useState<SurfaceBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => {
    if (season == null) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<SurfacePayload>(
          `/v1/dashboard/?scope=surface&season=${season}`,
        );
        setPayload(res.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "No se pudo cargar el panel de superficie",
        );
        setPayload(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [season]);

  const provinceData = useMemo(
    () =>
      (payload?.by_province ?? []).slice(0, 15).map((r) => ({
        name: r.province_name,
        Licenciada: +r.licensed.toFixed(1),
        Auditada: +r.audited.toFixed(1),
        Productiva: +r.productive.toFixed(1),
      })),
    [payload?.by_province],
  );

  // Fetch del desglose cuando se abre el modal.
  useEffect(() => {
    if (!breakdownProducer || season == null) return;
    const run = async () => {
      setBreakdownLoading(true);
      try {
        const res = await apiClient.get<SurfaceBreakdown>(
          `/v1/dashboard/?scope=producer_breakdown&season=${season}&producer=${breakdownProducer.id}`,
        );
        setBreakdown(res.data);
      } catch {
        setBreakdown(null);
      } finally {
        setBreakdownLoading(false);
      }
    };
    run();
  }, [breakdownProducer, season]);

  // Abrir el listado de audits filtrado por productor + campaña
  // (nueva pestaña).
  const openAuditsList = (producerName: string) => {
    if (!payload?.season) return;
    const params = new URLSearchParams();
    params.append("filters[0][field]", "q");
    params.append("filters[0][operator]", "contains");
    params.append("filters[0][value]", producerName);
    params.append("filters[1][field]", "season_year");
    params.append("filters[1][operator]", "eq");
    params.append("filters[1][value]", String(payload.season.year));
    window.open(`/audits?${params.toString()}`, "_blank");
  };

  // Dropdown-filter "Provincia" en el ranking de productores.
  const producerProvinceFilters = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ text: string; value: string }> = [];
    for (const row of payload?.by_producer ?? []) {
      const p = row.province_name || "—";
      if (!seen.has(p)) {
        seen.add(p);
        out.push({ text: p, value: p });
      }
    }
    return out.sort((a, b) => a.text.localeCompare(b.text));
  }, [payload?.by_producer]);

  const nettedData = useMemo(
    () => [
      {
        name: "Mallada",
        value: payload?.kpis.netted_ha ?? 0,
        color: SURFACE_COLORS.netted,
      },
      {
        name: "No mallada",
        value: Math.max(
          (payload?.kpis.licensed_ha ?? 0) - (payload?.kpis.netted_ha ?? 0),
          0,
        ),
        color: SURFACE_COLORS.notNetted,
      },
    ],
    [payload?.kpis],
  );

  const productiveData = useMemo(
    () => [
      {
        name: "Productiva",
        value: payload?.kpis.productive_ha ?? 0,
        color: SURFACE_COLORS.productive,
      },
      {
        name: "No productiva",
        value: Math.max(
          (payload?.kpis.audited_ha ?? 0) - (payload?.kpis.productive_ha ?? 0),
          0,
        ),
        color: SURFACE_COLORS.notNetted,
      },
    ],
    [payload?.kpis],
  );

  if (error) return <Alert type="error" message={error} showIcon />;

  return (
    <Spin spinning={loading} tip="Cargando superficie…">
      {payload && (
        <>
          {/* ── Fila 1 — KPIs ─────────────────────────────────── */}
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Superficie licenciada"
                  value={payload.kpis.licensed_ha}
                  precision={1}
                  suffix="ha"
                  prefix={<AreaChartOutlined />}
                  valueStyle={{ color: SURFACE_COLORS.licensed }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  farm.surface_tango de fincas con audit
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Superficie auditada"
                  value={payload.kpis.audited_ha}
                  precision={1}
                  suffix="ha"
                  prefix={<AreaChartOutlined />}
                  valueStyle={{ color: SURFACE_COLORS.audited }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Σ parcelas auditadas (audit_plot.surface)
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Superficie productiva"
                  value={payload.kpis.productive_ha}
                  precision={1}
                  suffix="ha"
                  prefix={<ExperimentOutlined />}
                  valueStyle={{ color: SURFACE_COLORS.productive }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Parcelas auditadas con kg_plot &gt; 0
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="% Mallada"
                  value={payload.kpis.netted_pct}
                  precision={1}
                  suffix="%"
                  prefix={<GatewayOutlined />}
                  valueStyle={{ color: SURFACE_COLORS.netted }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {fmtHa(payload.kpis.netted_ha)} bajo malla
                </Text>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 2 — Comparativo por provincia ────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Superficie por provincia · licenciada / auditada / productiva">
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart
                    data={provinceData}
                    margin={{ left: 10, right: 20, top: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) =>
                        v.toLocaleString("es-ES", {
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                    <Tooltip formatter={(v: any) => fmtHa(Number(v))} />
                    <Legend />
                    <Bar dataKey="Licenciada" fill={SURFACE_COLORS.licensed} />
                    <Bar dataKey="Auditada" fill={SURFACE_COLORS.audited} />
                    <Bar dataKey="Productiva" fill={SURFACE_COLORS.productive} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 3 — Donuts ─────────────────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card title="Mallada vs no mallada (sobre auditada)">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={nettedData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                      label={(e: any) => fmtHa(e.value)}
                    >
                      {nettedData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => fmtHa(Number(v))} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Productiva vs no productiva (sobre auditada)">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={productiveData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                      label={(e: any) => fmtHa(e.value)}
                    >
                      {productiveData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => fmtHa(Number(v))} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 4 — Top productores ──────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Top 25 productores por hectáreas licenciadas">
                <Table
                  size="small"
                  rowKey="producer_id"
                  dataSource={payload.by_producer}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 1100 }}
                  columns={[
                    {
                      title: "Productor",
                      dataIndex: "producer_name",
                      key: "producer_name",
                      sorter: (a, b) =>
                        a.producer_name.localeCompare(b.producer_name),
                      // Tag "Auditoría pendiente" si el productor
                      // aún tiene audits no finalizados en la
                      // campaña: señala que las cifras de audited/
                      // productive por fila están incompletas.
                      render: (name: string, r) => (
                        <Space size="small" wrap>
                          <span>{name}</span>
                          {r.has_pending_audits && (
                            <Tag
                              icon={<ClockCircleOutlined />}
                              color="orange"
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              Auditoría pendiente
                            </Tag>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: "Provincia",
                      dataIndex: "province_name",
                      key: "province_name",
                      sorter: (a, b) =>
                        a.province_name.localeCompare(b.province_name),
                      filters: producerProvinceFilters,
                      filterSearch: true,
                      onFilter: (value, record) =>
                        (record.province_name || "—") === value,
                    },
                    {
                      title: "Fincas",
                      dataIndex: "farms_count",
                      key: "farms_count",
                      width: 80,
                      align: "right",
                      sorter: (a, b) => a.farms_count - b.farms_count,
                    },
                    {
                      title: "Ha licenciadas",
                      dataIndex: "licensed_ha",
                      key: "licensed_ha",
                      width: 140,
                      align: "right",
                      sorter: (a, b) => a.licensed_ha - b.licensed_ha,
                      defaultSortOrder: "descend",
                      render: (v: number) => fmtHa(v),
                    },
                    {
                      title: "Ha auditadas",
                      dataIndex: "audited_ha",
                      key: "audited_ha",
                      width: 130,
                      align: "right",
                      sorter: (a, b) => a.audited_ha - b.audited_ha,
                      render: (v: number) => fmtHa(v),
                    },
                    {
                      title: "Ha productivas",
                      dataIndex: "productive_ha",
                      key: "productive_ha",
                      width: 140,
                      align: "right",
                      sorter: (a, b) => a.productive_ha - b.productive_ha,
                      render: (v: number) => fmtHa(v),
                    },
                    {
                      title: "% Mallada",
                      dataIndex: "netted_pct",
                      key: "netted_pct",
                      width: 110,
                      align: "right",
                      sorter: (a, b) => a.netted_pct - b.netted_pct,
                      render: (v: number) => `${v.toFixed(1)}%`,
                    },
                    {
                      title: "Acciones",
                      key: "actions",
                      width: 110,
                      fixed: "right",
                      render: (_: any, r) => (
                        <Space size="small">
                          <Button
                            size="small"
                            icon={<BarChartOutlined />}
                            title="Ver desglose por auditoría (ha por audit)"
                            onClick={() =>
                              setBreakdownProducer({
                                id: r.producer_id,
                                name: r.producer_name,
                              })
                            }
                          />
                          <Button
                            size="small"
                            icon={<LinkOutlined />}
                            title="Abrir listado de auditorías del productor en nueva pestaña"
                            onClick={() => openAuditsList(r.producer_name)}
                          />
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Modal "Ver desglose" — misma estructura que el de
          producción pero con columnas de superficie. Reusa el endpoint
          `/v1/dashboard/?scope=producer_breakdown` que ya devuelve
          tanto kg como ha por audit. */}
      <Modal
        open={!!breakdownProducer}
        onCancel={() => {
          setBreakdownProducer(null);
          setBreakdown(null);
        }}
        footer={null}
        width={960}
        title={
          breakdownProducer
            ? `Desglose por auditoría · ${breakdownProducer.name}`
            : "Desglose por auditoría"
        }
      >
        <Spin spinning={breakdownLoading}>
          <Table
            size="small"
            rowKey="audit_id"
            dataSource={breakdown?.audits ?? []}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 820 }}
            columns={[
              {
                title: "Finca",
                dataIndex: "farm_name",
                key: "farm_name",
                sorter: (a, b) => a.farm_name.localeCompare(b.farm_name),
              },
              {
                title: "Ref.",
                dataIndex: "reference",
                key: "reference",
                width: 110,
                render: (ref: string, r) => (
                  <a
                    href={`/audits/${r.audit_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ref}
                  </a>
                ),
              },
              {
                title: "Estado",
                dataIndex: "status",
                key: "status",
                width: 110,
                render: (v: string) => {
                  const map: Record<string, { color: string; label: string }> = {
                    planned: { color: "blue", label: "Planificada" },
                    performed: { color: "purple", label: "Ejecutada" },
                    submitted: { color: "gold", label: "Pdte. revisión" },
                    finalized: { color: "green", label: "Finalizada" },
                    canceled: { color: "red", label: "Cancelada" },
                  };
                  const m = map[v] ?? { color: "default", label: v };
                  return <Tag color={m.color}>{m.label}</Tag>;
                },
              },
              {
                title: "Fecha fin.",
                dataIndex: "finalized_at",
                key: "finalized_at",
                width: 110,
                render: (v: string | null) =>
                  v ? dayjs(v).format("DD/MM/YYYY") : "—",
              },
              {
                title: "Ha licenciada",
                dataIndex: "licensed_ha",
                key: "licensed_ha",
                width: 130,
                align: "right",
                sorter: (a, b) => a.licensed_ha - b.licensed_ha,
                render: (v: number) => fmtHa(v),
              },
              {
                title: "Ha auditada",
                dataIndex: "audited_ha",
                key: "audited_ha",
                width: 130,
                align: "right",
                sorter: (a, b) => a.audited_ha - b.audited_ha,
                render: (v: number) => fmtHa(v),
              },
              {
                title: "Ha productiva",
                dataIndex: "productive_ha",
                key: "productive_ha",
                width: 130,
                align: "right",
                sorter: (a, b) => a.productive_ha - b.productive_ha,
                render: (v: number) => fmtHa(v),
              },
              {
                title: "Mallada",
                dataIndex: "is_under_net",
                key: "is_under_net",
                width: 90,
                align: "center",
                render: (v: boolean) =>
                  v ? (
                    <Tag icon={<CheckOutlined />} color="orange">
                      Sí
                    </Tag>
                  ) : (
                    <span style={{ color: "#bfbfbf" }}>—</span>
                  ),
              },
            ]}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};
